import { ref, onUnmounted } from 'vue'
import { useRuntimeConfig } from '#app'
import { logError, debug } from '@/composables/useLogger'
import { useUserStore } from '@/stores/user'
import type { AudioNoteResponse } from '@/types/audio'

// Ensure these are initialized only on client side
let audioContext: AudioContext | null = null
let analyser: AnalyserNode | null = null
let microphone: MediaStreamAudioSourceNode | null = null
let animationFrame: number | null = null

// Declare global to fix TypeScript error
declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext
  }
}

export function useAudioNoteRecorder() {
  const config = useRuntimeConfig()
  const apiBase = config.public.apiBase
  const userStore = useUserStore()
  const isRecording = ref(false)
  const recordingTime = ref(0)
  const recordingStartTime = ref<number | null>(null)
  const mediaRecorder = ref<MediaRecorder | null>(null)
  const audioChunks = ref<Blob[]>([])
  const error = ref<string | null>(null)
  const audioLevel = ref(0)

  // Clean up on component unmount
  onUnmounted(() => {
    stopAudioMonitoring()
    stopRecording()
  })

  const updateAudioLevel = () => {
    if (!analyser || !audioContext) {
      debug('Skipping audio level update - no context:', {
        hasAnalyser: !!analyser,
        hasAudioContext: !!audioContext,
      })
      return
    }

    try {
      const array = new Uint8Array(analyser.frequencyBinCount)
      analyser.getByteFrequencyData(array)

      // Calculate RMS (Root Mean Square) for better volume representation
      const rms = Math.sqrt(array.reduce((sum, val) => sum + val * val, 0) / array.length)

      // More natural scaling (0-100)
      const normalizedLevel = Math.min(100, (rms / 128) * 100)

      // Only update if there's a significant change
      if (Math.abs(audioLevel.value - normalizedLevel) > 0.5) {
        debug('Audio level updated:', {
          rms,
          normalizedLevel,
          previous: audioLevel.value,
        })
        audioLevel.value = normalizedLevel
      }

      // Request next frame
      if (audioContext.state === 'running') {
        animationFrame = requestAnimationFrame(updateAudioLevel)
      } else {
        debug('Audio context not running:', audioContext.state)
      }
    } catch (error) {
      logError('Error in updateAudioLevel:', error)
    }
  }

  const startAudioMonitoring = (stream: MediaStream) => {
    try {
      // Ensure we're running on client side
      if (typeof window === 'undefined') {
        debug('Not starting audio monitoring - not on client side')
        return
      }

      // Close any existing audio context
      if (audioContext) {
        stopAudioMonitoring()
      }

      debug('Creating new AudioContext')
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      audioContext = new AudioContextClass()

      if (!audioContext) {
        throw new Error('Failed to create AudioContext')
      }

      analyser = audioContext.createAnalyser()
      microphone = audioContext.createMediaStreamSource(stream)

      // Optimized settings for speech visualization
      analyser.smoothingTimeConstant = 0.2
      analyser.fftSize = 1024
      analyser.minDecibels = -85
      analyser.maxDecibels = -10

      // Connect nodes
      microphone.connect(analyser)

      debug('Audio monitoring setup complete:', {
        sampleRate: audioContext.sampleRate,
        state: audioContext.state,
        analyserSettings: {
          smoothingTimeConstant: analyser.smoothingTimeConstant,
          fftSize: analyser.fftSize,
          minDecibels: analyser.minDecibels,
          maxDecibels: analyser.maxDecibels,
          frequencyBinCount: analyser.frequencyBinCount,
        },
      })

      // Ensure context is running before starting animation frame
      if (audioContext.state === 'suspended') {
        debug('Resuming suspended audio context')
        audioContext.resume().then(() => {
          debug('Audio context resumed, starting monitoring')
          if (audioContext) {
            // Check again after async operation
            animationFrame = requestAnimationFrame(updateAudioLevel)
          }
        })
      } else {
        debug('Starting audio monitoring')
        animationFrame = requestAnimationFrame(updateAudioLevel)
      }
    } catch (error) {
      logError('Error in startAudioMonitoring:', error)
      throw error
    }
  }

  const stopAudioMonitoring = () => {
    debug('Stopping audio monitoring:', {
      hasAnimationFrame: !!animationFrame,
      hasAudioContext: !!audioContext,
      contextState: audioContext?.state,
    })

    if (animationFrame) {
      cancelAnimationFrame(animationFrame)
      animationFrame = null
    }
    if (microphone) {
      microphone.disconnect()
      microphone = null
    }
    if (analyser) {
      analyser.disconnect()
      analyser = null
    }
    if (audioContext) {
      audioContext.close().catch((err) => logError('Error closing audio context:', err))
      audioContext = null
    }
    audioLevel.value = 0
  }

  const checkMicrophonePermission = async () => {
    try {
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName })
      if (result.state === 'denied') {
        throw new Error('Microphone access was denied. Please enable it in your browser settings.')
      }

      // Get audio stream to check mic
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })

      // Check if we're getting audio input
      const audioContext = new AudioContext()
      const analyser = audioContext.createAnalyser()
      const microphone = audioContext.createMediaStreamSource(stream)

      analyser.smoothingTimeConstant = 0.5
      analyser.fftSize = 256

      microphone.connect(analyser)

      return new Promise((resolve, reject) => {
        const silenceTimeout = setTimeout(() => {
          cleanup()
          reject(new Error('No audio input detected. Please check if your microphone is muted.'))
        }, 1000)

        // Use requestAnimationFrame for continuous monitoring
        let frame: number
        const checkAudio = () => {
          const array = new Uint8Array(analyser.frequencyBinCount)
          analyser.getByteFrequencyData(array)

          // Calculate RMS for better volume detection
          const rms = Math.sqrt(array.reduce((sum, val) => sum + val * val, 0) / array.length)

          if (rms > 1) {
            cleanup()
            resolve(true)
            return
          }

          frame = requestAnimationFrame(checkAudio)
        }

        checkAudio()

        // Cleanup function
        function cleanup() {
          clearTimeout(silenceTimeout)
          cancelAnimationFrame(frame)
          analyser.disconnect()
          microphone.disconnect()
          audioContext.close()
          stream.getTracks().forEach((track) => track.stop())
        }
      })
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          throw new Error(
            'Microphone access was denied. Please enable it in your browser settings.'
          )
        }
        if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          throw new Error('No microphone found. Please connect a microphone and try again.')
        }
      }
      throw error
    }
  }

  const startRecording = async (onResponse: (response: AudioNoteResponse) => void) => {
    try {
      // Check if user is logged in
      const isLoggedIn = await userStore.checkLoginStatus()
      if (!isLoggedIn) {
        throw new Error('You must be logged in to record audio notes')
      }

      // Check microphone permissions first
      await checkMicrophonePermission()

      // Reset state
      audioChunks.value = []
      error.value = null
      recordingStartTime.value = null
      audioLevel.value = 0

      // Set up audio stream with quality settings
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1,
        },
      })

      // Start audio monitoring first
      debug('Initializing audio monitoring')
      startAudioMonitoring(stream)

      // Set up MediaRecorder with high quality settings
      debug('Setting up MediaRecorder')
      mediaRecorder.value = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        bitsPerSecond: 128000,
      })

      // Handle data available event
      mediaRecorder.value.ondataavailable = (event) => {
        if (event.data.size > 0) {
          debug('Received audio chunk:', { size: event.data.size })
          audioChunks.value.push(event.data)
        }
      }

      // Handle recording stop
      mediaRecorder.value.onstop = async () => {
        try {
          debug('Processing recorded audio:', {
            chunks: audioChunks.value.length,
            totalSize: audioChunks.value.reduce((sum, chunk) => sum + chunk.size, 0),
          })

          // Create audio blob
          const audioBlob = new Blob(audioChunks.value, { type: 'audio/webm' })

          // Create form data
          const formData = new FormData()
          formData.append('audio_file', audioBlob)

          // Upload to FastAPI
          const response = await fetch(`${apiBase}/voice/notes`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
          })

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.detail || 'Failed to process audio')
          }

          const data = (await response.json()) as AudioNoteResponse

          // Call the response callback
          onResponse(data)
        } catch (err) {
          logError('Error processing audio:', err)
          error.value = err instanceof Error ? err.message : 'Failed to process audio'
        } finally {
          stopRecording()
        }
      }

      // Now that everything is set up, start recording
      debug('Starting MediaRecorder')
      recordingStartTime.value = Date.now()
      isRecording.value = true
      mediaRecorder.value.start(100) // Collect data more frequently (every 100ms)

      // Start the recording timer - update every 100ms for smoother display
      const timer = setInterval(() => {
        if (recordingStartTime.value) {
          recordingTime.value = Date.now() - recordingStartTime.value
        }
      }, 100)

      // Stop recording after 1 minute
      setTimeout(() => {
        if (isRecording.value) {
          stopRecording()
        }
      }, 60000)

      return () => {
        clearInterval(timer)
        stopRecording()
      }
    } catch (err) {
      logError('Error starting recording:', err)
      error.value = err instanceof Error ? err.message : 'Failed to start recording'
      stopRecording()
      throw err
    }
  }

  const stopRecording = () => {
    stopAudioMonitoring()

    if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
      mediaRecorder.value.stop()
      mediaRecorder.value.stream.getTracks().forEach((track) => track.stop())
      mediaRecorder.value = null
    }

    isRecording.value = false
    recordingTime.value = 0
    recordingStartTime.value = null
  }

  return {
    isRecording,
    recordingTime,
    error,
    audioLevel,
    startRecording,
    stopRecording,
  }
}
